import { FC, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Alert } from 'react-daisyui';
import {
  DocumentTextIcon,
  LightBulbIcon,
  HandThumbUpIcon,
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import useSessionDetail from '@/api/useSessionDetail.ts';
import useAuditExport from '@/api/useAuditExport.ts';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import GovernanceChainList from '@/components/ui/GovernanceChainList/GovernanceChainList.tsx';
import RiskFlagsList from '@/components/ui/RiskFlagsList/RiskFlagsList.tsx';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { formatGovScore } from '@/utils/sessionTableDisplay.ts';
import { resolveSessionCommitment } from '@/utils/resolveSessionCommitment.ts';
import { canExportAudit } from '@/utils/canExportAudit.ts';
import { preloadIntakeFromSession } from '@/utils/intakeDraftStorage.ts';
import { cn } from '@/utils/classNames.ts';
import type { Tables } from '$/database.types';
import type { RiskFlag } from '$/requests/GovernanceCheck.ts';

type LedgerEntry = Tables<'ledger_entries'>;
type ApprovalState = Tables<'approval_states'>;

type IntentPayload = { issue_title?: string; issue_text?: string };
type DiagnosticAiOutput = { diagnostic_summary?: string; assumption_record?: string };
type DiagnosticPayload = { user_reasoning_record?: string };
type RecommendationAiOutput = {
  recommended_direction?: { lead?: string; detail?: string };
  root_cause_diagnosis?: { lead?: string; detail?: string };
  action_plan?: { lead?: string; bullets?: string[] };
};
type CompassFilter = {
  name?: string;
  status?: 'passed' | 'caution' | 'escalation';
  framework_connection?: string;
  explanation?: string;
  next_session_recommendation?: string;
};
type GovernanceAiOutput = {
  compass_filters?: CompassFilter[];
  governance_helper_copy?: string;
  risk_flags?: RiskFlag[];
};
type ExecutionAssetOutput = {
  ledger_hash?: string;
  framework_label?: string;
  governance_score?: number;
  session_index?: number;
  hash_chain_valid?: boolean;
  status?: string;
};

function getStepAi<T>(entries: LedgerEntry[], step: string): T | null {
  const e = entries.find((x) => x.step === step);
  return e ? (e.ai_output as T) ?? null : null;
}

function getStepPayload<T>(entries: LedgerEntry[], step: string): T | null {
  const e = entries.find((x) => x.step === step);
  return e ? (e.payload as T) ?? null : null;
}

const COMPASS_STYLE = {
  passed: { card: 'bg-[#edfbf4]', title: 'text-[#00874c]', tag: 'Passed' },
  caution: { card: 'bg-[#fffdf0]', title: 'text-[#b38600]', tag: 'Caution' },
  escalation: { card: 'bg-[#fff1f0]', title: 'text-[#d93025]', tag: 'Escalation' },
} as const;

function compassStyle(status?: string) {
  return COMPASS_STYLE[(status as keyof typeof COMPASS_STYLE) ?? 'caution'] ?? COMPASS_STYLE.caution;
}

function sessionId(id: string): string {
  const year = new Date().getFullYear();
  const short = id.replace(/-/g, '').slice(0, 4).toUpperCase();
  return `CMD-${year}-${short}`;
}

function truncateHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function resolveRiskFlags(
  governanceEntry: LedgerEntry | undefined,
  governance: GovernanceAiOutput | null,
): RiskFlag[] {
  const fromColumn = governanceEntry?.risk_flags;
  if (Array.isArray(fromColumn) && fromColumn.length > 0) {
    return fromColumn as RiskFlag[];
  }
  return governance?.risk_flags ?? [];
}

function formatCategoryLabel(category: string): string {
  return category.replace(/_/g, ' ');
}

/** Story 14: escalation context + disposition when resolved (read-only, no admin actions). */
const EscalationSection: FC<{
  states: ApprovalState[];
  lifecycleStatus: string | null;
}> = ({ states, lifecycleStatus }) => {
  const { t } = useTranslation();
  const primary = states.find((s) => s.escalation_reason) ?? states[0];
  if (!primary && lifecycleStatus !== 'escalated') return null;

  const isPending =
    lifecycleStatus === 'escalated' ||
    states.some((s) => s.status === 'pending' || s.status === 'under_review');
  const resolved = states.find((s) => s.disposition && s.resolved_at);
  const statusKey = primary?.status
    ? (`sessionDetailPage.approvalStatus.${primary.status}` as const)
    : null;
  const statusLabel = statusKey ? t(statusKey, { defaultValue: primary?.status ?? '' }) : null;

  return (
    <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="font-newsreader text-[22px] font-extrabold text-[rgba(0,0,0,0.87)]">
          {t('sessionDetailPage.escalationStatusTitle')}
        </h2>
        {isPending && (
          <span className="font-newsreader text-[22px] font-extrabold text-[#b38600]">
            {t('sessionDetailPage.escalationStatusMarked')}
          </span>
        )}
      </div>
      {statusLabel && (
        <p className="font-dm text-sm text-[rgba(0,0,0,0.65)]">
          {t('sessionDetailPage.lifecycle')}: <strong>{statusLabel}</strong>
        </p>
      )}
      {primary?.escalation_category && (
        <p className="font-dm text-sm text-[rgba(0,0,0,0.65)]">
          {t('sessionDetailPage.escalationCategory')}:{' '}
          <strong>{formatCategoryLabel(primary.escalation_category)}</strong>
        </p>
      )}
      {primary?.created_at && (
        <p className="font-dm text-sm text-[rgba(0,0,0,0.65)]">
          {t('sessionDetailPage.submittedAt')}:{' '}
          <strong>{dayjs(primary.created_at).format('MMM D, YYYY · HH:mm')}</strong>
        </p>
      )}
      {primary?.escalation_reason && (
        <div>
          <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] mb-1">
            {t('sessionDetailPage.escalationReason')}
          </p>
          <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">{primary.escalation_reason}</p>
        </div>
      )}
      {resolved && (
        <div className="flex flex-col gap-1 font-dm text-sm text-[rgba(0,0,0,0.65)]">
          <p>
            {t('sessionDetailPage.disposition')}: <strong>{resolved.disposition}</strong>
            {resolved.reviewer_role && (
              <>
                {' '}
                · {t('sessionDetailPage.reviewerRole')}: <strong>{resolved.reviewer_role}</strong>
              </>
            )}
          </p>
          {resolved.resolved_at && (
            <p>
              {t('sessionDetailPage.resolvedAt')}:{' '}
              <strong>{dayjs(resolved.resolved_at).format('MMM D, YYYY · HH:mm')}</strong>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const SessionDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProfile } = useAuthContext();
  const [exportError, setExportError] = useState<string | null>(null);
  const { mutate: exportRecord, isPending: isExporting } = useAuditExport();

  const { data, isLoading, isError } = useSessionDetail(id ?? null);
  const mayExport = canExportAudit(userProfile);

  const derived = useMemo(() => {
    if (!data) return null;

    const { decision, ledger_entries, approval_states, compass_preview_cache, audit_artifacts } = data;
    const intent = getStepPayload<IntentPayload>(ledger_entries, 'intent_captured');
    const diagnosticAi = getStepAi<DiagnosticAiOutput>(ledger_entries, 'diagnostic');
    const diagnosticPayload = getStepPayload<DiagnosticPayload>(ledger_entries, 'diagnostic');
    const recommendation = getStepAi<RecommendationAiOutput>(ledger_entries, 'recommendation');
    const governanceEntry = ledger_entries.find((e) => e.step === 'governance_check');
    const governance = getStepAi<GovernanceAiOutput>(ledger_entries, 'governance_check');
    const riskFlags = resolveRiskFlags(governanceEntry, governance);
    const executionEntry = ledger_entries.find((e) => e.step === 'execution_asset');
    const executionAsset = (executionEntry?.ai_output as ExecutionAssetOutput | undefined) ?? null;
    const commitment = resolveSessionCommitment(ledger_entries, compass_preview_cache);

    const missing: string[] = [];
    if (!intent?.issue_text) missing.push('issueFraming');
    if (!diagnosticAi?.diagnostic_summary) missing.push('diagnosticInterpretation');
    if (!recommendation) missing.push('recommendation');
    if (!governance?.compass_filters?.length) missing.push('compassReview');
    if (decision.status === 'execution_asset_produced' && !executionAsset) missing.push('sealedTitle');
    if (
      (decision.status === 'escalated' || approval_states.length > 0) &&
      !approval_states.some((s) => s.escalation_reason)
    ) {
      missing.push('escalationStatusTitle');
    }

    return {
      decision,
      ledger_entries,
      approval_states,
      audit_artifacts,
      intent,
      diagnosticAi,
      diagnosticPayload,
      recommendation,
      governance,
      riskFlags,
      executionEntry,
      executionAsset,
      commitment,
      missingSectionKeys: missing,
      showSealed:
        decision.status === 'execution_asset_produced' || Boolean(executionEntry || executionAsset),
    };
  }, [data]);

  if (isLoading) return <LoaderWrapper isLoading />;
  if (isError || !data || !derived) {
    return (
      <p className="font-dm text-sm text-center py-20 text-[#2b3440]/50">
        {t('sessionDetailPage.notFound')}
      </p>
    );
  }

  const {
    decision,
    approval_states,
    audit_artifacts,
    intent,
    diagnosticAi,
    diagnosticPayload,
    recommendation,
    governance,
    riskFlags,
    executionEntry,
    executionAsset,
    commitment,
    missingSectionKeys,
    showSealed,
    ledger_entries,
  } = derived;

  const missingSections = missingSectionKeys.map((key) =>
    t(`sessionDetailPage.${key}` as 'sessionDetailPage.issueFraming'),
  );

  const handleStartFromContext = () => {
    preloadIntakeFromSession(decision.issue_title, intent?.issue_text);
    navigate(Routes.COMMON_INTAKE);
  };

  const handleExport = () => {
    if (!id || !mayExport) return;
    setExportError(null);
    exportRecord(id, {
      onError: (err) => {
        setExportError(err instanceof Error ? err.message : t('sessionDetailPage.exportFailed'));
      },
    });
  };

  const lifecycleKey = `sessionDetailPage.lifecycleStatus.${decision.status ?? ''}` as const;
  const lifecycleLabel = t(lifecycleKey, { defaultValue: decision.status ?? '—' });
  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center px-6 py-4 bg-[#f4f9ff] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">
            {t('sessionDetailPage.title')}
          </h1>
          <p className="font-dm text-[14px] font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            {t('sessionDetailPage.subtitle')}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-6 py-4 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
        <MetaItem icon={<DocumentTextIcon className="w-4 h-4 shrink-0" />} label={decision.issue_title ?? '—'} />
        <Sep />
        <MetaItem
          icon={<CircleStackIcon className="w-4 h-4 shrink-0" />}
          label={t('sessionDetailPage.frameworkApplied', {
            framework: decision.primary_framework ?? '—',
          })}
        />
        <Sep />
        <MetaItem
          icon={<ShieldCheckIcon className="w-4 h-4 shrink-0" />}
          label={
            <>
              {t('sessionDetailPage.governanceScore')}{' '}
              <strong>{formatGovScore(decision.governance_score)}</strong>
            </>
          }
        />
        <Sep />
        <MetaItem icon={<InformationCircleIcon className="w-4 h-4 shrink-0" />} label={<strong>{lifecycleLabel}</strong>} />
        <Sep />
        <MetaItem
          label={
            decision.completed_at
              ? dayjs(decision.completed_at).format('MMM D, YYYY')
              : dayjs(decision.created_at).format('MMM D, YYYY')
          }
        />
      </div>

      {missingSections.length > 0 && (
        <Alert status="warning" className="rounded-xl">
          <span className="font-dm text-sm">
            {t('sessionDetailPage.partialDataWarning')}{' '}
            {t('sessionDetailPage.partialDataHint', { sections: missingSections.join(', ') })}
          </span>
        </Alert>
      )}

      {intent?.issue_text && (
        <Card icon={<DocumentTextIcon className="w-5 h-5" />} title={t('sessionDetailPage.issueFraming')}>
          <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">{intent.issue_text}</p>
        </Card>
      )}

      {diagnosticAi?.diagnostic_summary && (
        <Card icon={<LightBulbIcon className="w-5 h-5" />} title={t('sessionDetailPage.diagnosticInterpretation')}>
          <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">
            {diagnosticAi.diagnostic_summary}
          </p>
        </Card>
      )}

      {diagnosticPayload?.user_reasoning_record && (
        <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <p className="font-dm text-sm font-bold text-[rgba(0,0,0,0.87)]">{t('sessionDetailPage.learningNote')}</p>
          <blockquote className="font-dm text-sm italic text-[rgba(0,0,0,0.7)] border-l-2 border-[rgba(0,0,0,0.15)] pl-4 leading-relaxed">
            "{diagnosticPayload.user_reasoning_record}"
          </blockquote>
          <p className="font-dm text-xs text-[rgba(0,0,0,0.4)] italic">
            {t('sessionDetailPage.learningNoteCaption')}
          </p>
        </div>
      )}

      {recommendation && (
        <Card icon={<HandThumbUpIcon className="w-5 h-5" />} title={t('sessionDetailPage.recommendation')}>
          {recommendation.recommended_direction?.lead && (
            <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">
              {recommendation.recommended_direction.lead}
            </p>
          )}

          {recommendation.root_cause_diagnosis?.lead && (
            <div className="mt-4">
              <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] uppercase tracking-wider mb-1">
                {t('sessionDetailPage.rootCause')}
              </p>
              <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">
                {recommendation.root_cause_diagnosis.lead}
              </p>
            </div>
          )}

          {recommendation.action_plan && (
            <div className="mt-4">
              <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] uppercase tracking-wider mb-1">
                {t('sessionDetailPage.actionPlan')}
              </p>
              {recommendation.action_plan.lead && (
                <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed mb-1">
                  {recommendation.action_plan.lead}
                </p>
              )}
              {(recommendation.action_plan.bullets ?? []).map((b, i) => (
                <p key={i} className="font-dm text-sm text-[rgba(0,0,0,0.7)]">
                  • {b}
                </p>
              ))}
            </div>
          )}
        </Card>
      )}

      {commitment && (
        <Card icon={<ClipboardDocumentCheckIcon className="w-5 h-5" />} title={t('sessionDetailPage.strategicCommitment')}>
          <p className="font-dm text-sm text-[rgba(0,0,0,0.7)]">
            {t('sessionDetailPage.commitmentConfidence')}:{' '}
            <strong>{t('sessionDetailPage.commitmentConfidenceValue', { level: commitment.confidence })}</strong>
          </p>
          <div className="mt-3">
            <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] uppercase tracking-wider mb-1">
              {t('sessionDetailPage.commitmentAssumption')}
            </p>
            <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed border-l-2 border-[rgba(0,0,0,0.12)] pl-3">
              {commitment.assumption}
            </p>
          </div>
        </Card>
      )}

      {governance?.compass_filters && governance.compass_filters.length > 0 && (
        <div className="flex flex-col gap-5 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <div>
            <h2 className="font-newsreader text-[22px] font-extrabold text-[rgba(0,0,0,0.87)]">
              {t('sessionDetailPage.compassReview')}
            </h2>
            {governance.governance_helper_copy && (
              <p className="font-dm text-sm text-[rgba(0,0,0,0.6)] mt-1 leading-relaxed">
                {governance.governance_helper_copy}
              </p>
            )}
          </div>
          {riskFlags.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="font-dm text-xs font-bold text-[rgba(0,0,0,0.45)] uppercase tracking-wider">
                {t('sessionDetailPage.riskFlagsTitle')}
              </p>
              <RiskFlagsList flags={riskFlags} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {governance.compass_filters.map((f, i) => {
              const s = compassStyle(f.status);
              return (
                <div key={i} className={cn('rounded-xl p-4 flex flex-col gap-2', s.card)}>
                  <p className={cn('font-dm text-sm font-bold', s.title)}>
                    {f.name} – {s.tag}
                  </p>
                  {f.framework_connection && (
                    <p className="font-dm text-xs font-semibold text-[rgba(0,0,0,0.45)]">
                      ZDE Framework Connection
                    </p>
                  )}
                  {f.explanation && (
                    <p className="font-dm text-xs text-[rgba(0,0,0,0.65)] leading-relaxed">
                      {f.explanation}
                    </p>
                  )}
                  {f.next_session_recommendation && (
                    <p className="font-dm text-xs text-[rgba(0,0,0,0.45)] leading-relaxed italic mt-1">
                      {f.next_session_recommendation}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <EscalationSection states={approval_states} lifecycleStatus={decision.status} />

      {showSealed && (
        <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[#C8E6C9] bg-[#E8F5E9]">
          <div className="flex items-center gap-2 text-[#2E7D32]">
            <CheckCircleIcon className="w-5 h-5 shrink-0" />
            <h2 className="font-newsreader text-[22px] font-extrabold">{t('sessionDetailPage.sealedTitle')}</h2>
          </div>
          <p className="font-dm text-sm text-[rgba(0,0,0,0.7)] leading-relaxed">
            {t('sessionDetailPage.sealedDescription')}
          </p>
          <div className="flex flex-col gap-1 font-dm text-sm text-[rgba(0,0,0,0.65)]">
            {executionAsset?.framework_label && (
              <p>
                {t('sessionDetailPage.framework')}: <strong>{executionAsset.framework_label}</strong>
              </p>
            )}
            {executionAsset?.governance_score != null && (
              <p>
                {t('sessionDetailPage.governanceScore')}:{' '}
                <strong>{formatGovScore(executionAsset.governance_score)}</strong>
              </p>
            )}
            {executionAsset?.session_index != null && (
              <p>
                {t('sessionDetailPage.sessionIndex')}: <strong>{executionAsset.session_index}</strong>
              </p>
            )}
            {(executionAsset?.ledger_hash ?? executionEntry?.hash) && (
              <p className="font-mono text-xs break-all">
                {t('sessionDetailPage.ledgerHashShort')}:{' '}
                <strong title={executionAsset?.ledger_hash ?? executionEntry?.hash}>
                  {truncateHash(executionAsset?.ledger_hash ?? executionEntry?.hash ?? '')}
                </strong>
              </p>
            )}
          </div>
        </div>
      )}

      {ledger_entries.length > 0 && (
        <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <div className="flex flex-col gap-1">
            <h2 className="font-newsreader text-[22px] font-extrabold text-[rgba(0,0,0,0.87)]">
              {t('governanceChain.title')}
            </h2>
            <p className="font-dm text-sm text-[rgba(0,0,0,0.55)] leading-relaxed">
              {t('governanceChain.description')}
            </p>
          </div>
          <GovernanceChainList entries={ledger_entries} />
        </div>
      )}

      {audit_artifacts.length > 0 && (
        <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
          <h2 className="font-newsreader text-[22px] font-extrabold text-[rgba(0,0,0,0.87)]">
            {t('sessionDetailPage.auditArtifacts')}
          </h2>
          <div className="flex flex-col gap-2">
            {audit_artifacts.map((artifact) => (
              <div
                key={artifact.id}
                className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border border-[rgba(204,204,204,0.4)]"
              >
                <div className="flex items-center gap-2">
                  <span className="font-dm text-sm font-semibold text-[rgba(0,0,0,0.75)]">
                    {artifact.artifact_type.replace(/_/g, ' ')}
                  </span>
                  {artifact.exportable && (
                    <span className="font-dm text-[11px] uppercase tracking-wide font-bold text-[#1a7a50] bg-[#d4f4e8] px-2 py-0.5 rounded-full">
                      {t('sessionDetailPage.exportable')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-right">
                  {artifact.ledger_hash && (
                    <span className="font-mono text-[11px] text-[rgba(0,0,0,0.4)]" title={artifact.ledger_hash}>
                      {truncateHash(artifact.ledger_hash)}
                    </span>
                  )}
                  <span className="font-dm text-xs text-[rgba(0,0,0,0.45)]">
                    {dayjs(artifact.generated_at).format('MMM D, YYYY')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
        <div className="flex items-center gap-2 text-[rgba(0,0,0,0.87)]">
          <DocumentTextIcon className="w-5 h-5" />
          <h2 className="font-newsreader text-[22px] font-extrabold">{t('sessionDetailPage.sessionMetadata')}</h2>
        </div>
        <div className="flex flex-col gap-1 font-dm text-sm text-[rgba(0,0,0,0.7)]">
          <p>
            {t('sessionDetailPage.framework')}: <strong>{decision.primary_framework ?? '—'}</strong>
          </p>
          <p>
            {t('sessionDetailPage.governanceScore')}: <strong>{formatGovScore(decision.governance_score)}</strong>
          </p>
          <p>
            {t('sessionDetailPage.sessionId')}: <strong>{sessionId(decision.id)}</strong>
          </p>
        </div>
      </div>

      {exportError && (
        <Alert status="error" className="rounded-xl">
          <span className="font-dm text-sm">{exportError}</span>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 pb-4">
        <button
          type="button"
          onClick={handleStartFromContext}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0f1b2a] text-white font-dm text-sm font-bold hover:bg-[#3D5585] transition-colors"
        >
          <PlusCircleIcon className="w-4 h-4" />
          {t('sessionDetailPage.startNewFromContext')}
        </button>
        {mayExport && (
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-[rgba(204,204,204,0.7)] font-dm text-sm font-bold text-[rgba(0,0,0,0.65)] hover:bg-[#f4f9ff] transition-colors disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {isExporting ? '…' : t('sessionDetailPage.exportRecord')}
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate(Routes.SESSION_ARCHIVE)}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-[rgba(204,204,204,0.7)] font-dm text-sm font-bold text-[rgba(0,0,0,0.65)] hover:bg-[#f4f9ff] transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t('sessionDetailPage.backToArchive')}
        </button>
      </div>
    </div>
  );
};

const Card: FC<{ icon?: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-[rgba(204,204,204,0.5)] bg-white">
    <div className="flex items-center gap-2 text-[rgba(0,0,0,0.87)]">
      {icon}
      <h2 className="font-newsreader text-[22px] font-extrabold">{title}</h2>
    </div>
    {children}
  </div>
);

const MetaItem: FC<{ icon?: React.ReactNode; label: React.ReactNode }> = ({ icon, label }) => (
  <div className="flex items-center gap-1.5 font-dm text-sm text-[rgba(0,0,0,0.65)]">
    {icon}
    <span>{label}</span>
  </div>
);

const Sep: FC = () => (
  <span className="text-[rgba(0,0,0,0.2)] select-none font-dm text-sm">·</span>
);

export default SessionDetailPage;
